/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * dailyMode — the date rules behind attaching many runs to one entry.
 *
 * WHY THIS EXISTS. A race has one official time per entrant, and event_result
 * says so in the database (one live row per registration). An event that runs
 * for days is different: the organizer attaches every run the entrant does, on
 * any day. Those runs live in event_result_split — one row per attached run —
 * so the database did not have to change. This file is the single place that
 * says when an event is "daily" and which day of it a run belongs to; the hook
 * (attach_validation_hook.py) and the saved queries repeat the same formula.
 *
 * INSTANTS, READ ON THE VENUE'S CALENDAR. An event's start and end are stored
 * as UTC instants — buildEventBatch converts the wall clock the organizer typed
 * with the event's timezone — and a run's started_at is naive UTC too. A "day"
 * is a date on the VENUE's calendar, so every date here is taken through the
 * event's timezone. Strings are never passed to `new Date()` bare: the browser
 * would read a zone-less string as ITS local time.
 */

export const MAX_EVENT_DAYS = 99;

const DAY_MS = 24 * 60 * 60 * 1000;
const LABEL_RE = /^(\d{2})\|([0-9a-f-]{36})$/;

/** "2026-10-27 12:55:00" or "2026-10-27T12:55:00" → ms, read as if UTC. */
function wallClockMs(value) {
  const [datePart, timePart = '00:00:00'] = String(value || '').trim().split(/[ T]/);
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh = 0, mm = 0, ss = 0] = timePart.replace(/Z$/, '').split(':').map((n) => Number.parseFloat(n) || 0);
  return Date.UTC(y, m - 1, d, hh, mm, ss);
}

/** The date part of a 'YYYY-MM-DD…' string, as a UTC midnight in ms. */
function dateMs(value) {
  const [datePart] = String(value || '').trim().split(/[ T]/);
  const [y, m, d] = datePart.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/** The venue-calendar date of a naive-UTC instant, as a UTC midnight in ms. */
function localDateMs(utcValue, timezone) {
  const local = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(wallClockMs(utcValue)));
  return dateMs(local);
}

/**
 * True when the event covers more than one calendar day AND lasts more than
 * 24 hours. The second half keeps a 10 pm–2 am night run a one-day race.
 */
export function isMultiDayEvent(startDatetime, endDatetime, timezone) {
  if (!startDatetime || !endDatetime) return false;
  const differentDates = localDateMs(startDatetime, timezone) !== localDateMs(endDatetime, timezone);
  return differentDates && (wallClockMs(endDatetime) - wallClockMs(startDatetime)) > DAY_MS;
}

/** The last day number of the event, counting its start date as day 1. */
export function eventDayCount(startDatetime, endDatetime, timezone) {
  return Math.round((localDateMs(endDatetime, timezone) - localDateMs(startDatetime, timezone)) / DAY_MS) + 1;
}

/** Which day of the event a run belongs to, on the venue's calendar. */
export function eventDayNumber(activityStartedAtUtc, eventStartDatetime, timezone) {
  const runDay = localDateMs(activityStartedAtUtc, timezone);
  return Math.round((runDay - localDateMs(eventStartDatetime, timezone)) / DAY_MS) + 1;
}

/** 'YYYY-MM-DD' (venue calendar) of day `dayNumber` of an event. */
export function eventDayDate(dayNumber, eventStartDatetime, timezone) {
  return new Date(localDateMs(eventStartDatetime, timezone) + (dayNumber - 1) * DAY_MS).toISOString().slice(0, 10);
}

/**
 * The event a race belongs to, from the board index ({ events, races } as
 * fetchEventActivities answers it), or null while that is still loading.
 */
export function eventOfRace(index, raceId) {
  const race = (index?.races || []).find((r) => String(r.id) === String(raceId));
  if (!race) return null;
  const eventId = race.event_id && typeof race.event_id === 'object' ? race.event_id.id : race.event_id;
  return (index?.events || []).find((e) => String(e.id) === String(eventId)) || null;
}

/** "07|<activity uuid>" — the label an attached run is stored under. */
export function encodeAttachLabel(dayNumber, activityId) {
  if (!Number.isInteger(dayNumber) || dayNumber < 1 || dayNumber > MAX_EVENT_DAYS) {
    throw new RangeError(`Event day ${dayNumber} is outside 1–${MAX_EVENT_DAYS}.`);
  }
  return `${String(dayNumber).padStart(2, '0')}|${activityId}`;
}

/** The day and run behind a label, or null for any label that is not ours. */
export function decodeAttachLabel(label) {
  const match = LABEL_RE.exec(String(label || ''));
  if (!match) return null;
  return { dayNumber: Number(match[1]), activityId: match[2] };
}
